"use client";

import { cn } from "@/lib/components";
import Logo from "@/svg/logo.svg";
import { ChevronRightIcon, MenuIcon, XIcon } from "lucide-react";
import Image from "next/image";
import { useSearchParams } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import { Button } from "../base/button";
import data from "./Header.data";
import { Nav } from "./Nav";

export function HeaderSidebar({
  triggerClassName,
}: {
  triggerClassName?: string;
}) {
  const [isOpen, setisOpen] = useState(() => false);
  const p = useSearchParams();

  useEffect(() => {
    setisOpen(false);
  }, [p]);

  const closeSidebar = useCallback(() => {
    setisOpen(false);
  }, []);

  const expandedLinks = Object.values(data.links).filter(
    (link) => link.subLinks,
  );
  const collapsedLinks = Object.values(data.links).filter(
    (link) => !link.subLinks,
  );

  return (
    <>
      <Button variant="ghost" size="icon" onClick={() => setisOpen((s) => !s)}>
        <MenuIcon className={cn(triggerClassName, "size-10")} />
      </Button>

      <div
        onClick={closeSidebar}
        className={cn(
          "fixed top-0 left-0 z-20 h-screen w-screen",
          "text-foreground bg-black/50",
          isOpen ? "visible opacity-100" : "invisible opacity-0",
          "transition-all duration-300",
        )}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "h-full w-full bg-white p-16 shadow-lg sm:w-xl",
            "transition-transform duration-300",
            isOpen ? "translate-x-0" : "-translate-x-full",
          )}
        >
          <div className="flex h-16 items-center justify-between">
            <Logo className="h-10" />
            <Button size="icon" variant="ghost" onClick={closeSidebar}>
              <XIcon className="size-10 hover:opacity-70" />
            </Button>
          </div>

          {expandedLinks.map((link) => (
            <Nav
              key={link.label}
              className={cn(
                "border-border border-b py-16",
                "flex flex-col gap-8",
              )}
            >
              <h2 className="font-bold underline">{link.label}</h2>
              {link.subLinks?.map((subLink) => (
                <NavSubLink
                  key={subLink.label}
                  href={`${link.path}${subLink.path}`}
                  title={subLink.label}
                  src={subLink.sidebarImage}
                />
              ))}
            </Nav>
          ))}

          <Nav className="flex flex-col gap-4 py-8">
            {collapsedLinks.map((link) => (
              <Nav.Link key={link.label} href={link.path}>
                {link.label}
              </Nav.Link>
            ))}
          </Nav>
        </div>
      </div>
    </>
  );
}

function NavSubLink({
  href,
  title,
  src,
}: {
  href: string;
  title: string;
  src: string;
  onClick?: () => void;
}) {
  return (
    <Nav.Link href={href} className={cn("flex items-center gap-4", "w-full")}>
      <Image
        src={src}
        alt={title}
        width={50}
        height={50}
        className="h-auto w-12 rounded-full"
      />
      <p className="flex-1">{title}</p>
      <ChevronRightIcon className="size-4" />
    </Nav.Link>
  );
}
